<template>
  <div v-if="!pendingPdf" class="relative">
    <DesertMessage
      v-if="isEformNotVisible"
      class="bg-white"
      :mainTitle="$t('message.documentDetail.empty.title')"
      :mainContent="$t('message.documentDetail.empty.content')"
    />
    <div v-else>
      <div v-if="isDocumentError" class="w-full absolute top-0 z-10 p-4">
        <zt-alert color="danger" filled :closeIcon="false">
          <template v-slot:custom-content>
            <div class="flex justify-start items-center p-2">
              <zt-icon name="info-circle-solid" class="text-5xl mr-4"></zt-icon>
              <p v-if="isDocumentCancelled" class="font-normal">
                {{ $t('message.documentDetail.emptyError.contentCancelled') }}
              </p>
              <p v-else class="font-normal">
                {{ $t('message.documentDetail.emptyError.content') }}
              </p>
            </div>
          </template>
        </zt-alert>
      </div>
      <div
        v-if="!pendingPdf"
        class="viewer-container relative"
        :style="{ width: width }"
      >
        <zt-pdf-viewer
          :key="reRenderKey"
          :fixed-rotation="false"
          :scale="0.5"
          :page="curPage"
          :src="pdf"
          :min-zoom="0.45"
          :rotate="rotation"
          @viewer-update="normalizeRotation"
          @page-count="totalsPages = $event"
        />
        <div
          v-if="totalsPages > 0"
          class="paginate flex absolute top-0 mt-4 bg-zt-dark-300 text-zt-dark-200 rounded p-2"
        >
          <button @click="prevPage"><zt-icon name="angle-left" /></button>
          <div class="mx-4">{{ curPage }} de {{ totalsPages }}</div>
          <button @click="nextPage"><zt-icon name="angle-right" /></button>
        </div>
      </div>
    </div>
  </div>
  <div class="viewer-container relative" :style="{ width: width }" v-else>
    <vue-element-loading
      :active="true"
      spinner="spinner"
      duration="1.5"
      color="#4f4f4f"
      size="50"
    />
  </div>
</template>

<script>
import { isEmpty } from 'lodash'
import VueElementLoading from 'vue-element-loading'
import DesertMessage from '@/components/generics/DesertMessage404.vue'
import User from '@/apiClient/User'
export default {
  inject: ['stateDetailStore'],
  components: {
    DesertMessage,
    VueElementLoading
  },
  data() {
    return {
      documentNotLoadError: false,
      curPage: 1,
      pdf: '',
      width: '',
      rotation: 0,
      pendingPdf: true,
      totalsPages: 0,
      reRenderKey: 0
    }
  },
  computed: {
    isEformNotVisible() {
      return (
        this.stateDetailStore.detail.dataFile.typeDocument === 'eform' &&
        isEmpty(this.stateDetailStore.detail.dataFile.ext) &&
        !this.isDocumentCancelled
      )
    },
    isDocumentError() {
      return this.documentNotLoadError && !this.pendingPdf
    },
    isDocumentCancelled() {
      return this.stateDetailStore.detail.document.estado === 'ANULADO'
    },
    applicationId() {
      return this.$store.state.user.aplicacion.id
    }
  },
  watch: {
    stateDetailStore: {
      handler() {
        if (this.stateDetailStore.detail.document.estado === 'ANULADO') {
          this.getDocument()
          this.reRenderKey = Date.now()
        }
      },
      deep: true
    }
  },
  async created() {
    this.userApi = new User()
    await this.getDocument()
    const viewerContainer = document.getElementById('view-doc')
    if (!viewerContainer) {
      return null
    }
    const { width } = viewerContainer.getBoundingClientRect()
    this.width = width
  },
  methods: {
    async getDocument() {
      if (this.isEformNotVisible) {
        this.pendingPdf = false
        return
      }
      if (this.isDocumentCancelled) {
        this.pdf = ''
        this.pendingPdf = false
        this.documentNotLoadError = true
        return
      }
      this.pendingPdf = true
      try {
        const { data: data } = await this.userApi.getDocumentPDF(
          this.applicationId,
          this.stateDetailStore.detail.idDocument
        )
        if (this.isPdf(data)) {
          const base64 = new Buffer(data, 'binary').toString('base64')
          this.pdf = `data:application/pdf;base64,${base64}`
          this.documentNotLoadError = false
          return
        }
        this.documentNotLoadError = true
      } catch (error) {
        this.documentNotLoadError = true
        this.pdfLoaded = false
      } finally {
        this.pendingPdf = false
      }
    },
    isPdf(buffer) {
      const pdfHeader = [0x25, 0x50, 0x44, 0x46] // %PDF
      const uint8Array = new Uint8Array(buffer)
      return (
        buffer.byteLength >= 4 &&
        pdfHeader.every((byte, i) => uint8Array[i] === byte)
      )
    },
    nextPage() {
      if (this.curPage < this.totalsPages) this.curPage++
    },
    prevPage() {
      if (this.curPage > 1) this.curPage--
    },
    normalizeRotation(doc) {
      if (doc.page.pdfPageRotate > 0) {
        this.rotation = doc.page.pdfPageRotate * -1
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.viewer-container {
  height: calc(100vh - 185px);
}
.paginate {
  left: 50%;
  transform: translate(-50%, 0);
}
.zt-pdf-viewer ::-webkit-scrollbar-track {
  background-color: #c4c4c4;
}
.zt-pdf-viewer ::-webkit-scrollbar {
  width: 0.625rem;
  height: 0.625rem;
}
.zt-pdf-viewer ::-webkit-scrollbar-thumb {
  border-radius: 5px;
  background-color: #888888;
}
</style>
